
import React, { useState } from 'react';
import {
  Search,
  Shield,
  Users,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Crown,
  Eye,
  UserCheck
} from 'lucide-react';
import { RoleDetail, convertLegacyToEnhanced } from '../../../types/settings/RoleTypes';
import { RoleLevel, LeadScope } from '../../../types/settings/EnhancedRoleTypes';
import { PaginationProps } from '../../../types/settings/settings';

interface ModernRolesTableProps {
  initialData: RoleDetail[];
  onEdit?: (role: RoleDetail) => void;
  pagination?: PaginationProps;
  onPageChange?: (page: number) => void;
  onSearch?: (term: string) => Promise<void>;
  onSort?: (column: string, order: 'ASC' | 'DESC') => Promise<void>;
  isLoading?: boolean;
  searchTerm?: string;
  sortColumn?: string;
  sortOrder?: 'ASC' | 'DESC';
}

const ModernRolesTable: React.FC<ModernRolesTableProps> = ({
  initialData,
  onEdit,
  pagination,
  onPageChange,
  onSearch,
  onSort,
  isLoading = false,
  searchTerm = '',
  sortColumn,
  sortOrder
}) => {
  const [searchInput, setSearchInput] = useState(searchTerm);

  // Helper functions for role display
  const getRoleLevelInfo = (level?: RoleLevel) => {
    switch (level) {
      case RoleLevel.ADMIN:
        return { label: 'Administrator', color: 'text-red-600 bg-red-50 border-red-200', icon: Crown };
      case RoleLevel.MANAGER:
        return { label: 'Manager', color: 'text-purple-600 bg-purple-50 border-purple-200', icon: Users };
      case RoleLevel.TEAM_LEAD:
        return { label: 'Team Lead', color: 'text-brand-600 bg-brand-50 border-brand-200', icon: UserCheck };
      case RoleLevel.SALES_REP:
        return { label: 'Sales Rep', color: 'text-green-600 bg-green-50 border-green-200', icon: Shield };
      case RoleLevel.VIEW_ONLY:
        return { label: 'View Only', color: 'text-gray-600 bg-gray-50 border-gray-200', icon: Eye };
      default:
        return { label: 'Unknown', color: 'text-gray-600 bg-gray-50 border-gray-200', icon: Shield };
    }
  };

  const getLeadScopeInfo = (scope?: LeadScope) => {
    switch (scope) {
      case LeadScope.ALL:
        return { label: 'All Leads', color: 'text-green-700 bg-green-50' };
      case LeadScope.TEAM:
        return { label: 'Team Leads', color: 'text-brand-700 bg-brand-50' };
      case LeadScope.ASSIGNED:
        return { label: 'Assigned Only', color: 'text-orange-700 bg-orange-50' };
      case LeadScope.DEPARTMENT:
        return { label: 'Department', color: 'text-purple-700 bg-purple-50' };
      case LeadScope.NONE:
        return { label: 'No Access', color: 'text-gray-700 bg-gray-50' };
      default:
        return { label: 'Unknown', color: 'text-gray-700 bg-gray-50' };
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      await onSearch(searchInput);
    }
  };

  const handleSort = (column: string) => {
    if (onSort) {
      const newOrder = column === sortColumn && sortOrder === 'ASC' ? 'DESC' : 'ASC';
      onSort(column, newOrder);
    }
  };

  const SortButton = ({ column, label }: { column: string; label: string }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
    >
      <span>{label}</span>
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  if (isLoading) {
    return <RoleTableSkeleton />;
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Search Bar */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search roles by name or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <SortButton column="role_name" label="Role" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <SortButton column="role_level" label="Level" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <SortButton column="lead_scope" label="Lead Access" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <SortButton column="role_description" label="Description" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {initialData.map((role) => {
              // Convert legacy role to enhanced format for display
              const enhancedRole = role.role_level ? role : convertLegacyToEnhanced(role);
              const levelInfo = getRoleLevelInfo(enhancedRole.role_level);
              const scopeInfo = getLeadScopeInfo(enhancedRole.lead_scope);
              const LevelIcon = levelInfo.icon;

              return (
                <tr key={role.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  {/* Role Info */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className={`h-10 w-10 rounded-full ${levelInfo.color.split(' ')[1]} ${levelInfo.color.split(' ')[2]} flex items-center justify-center border`}>
                          <LevelIcon className={`h-5 w-5 ${levelInfo.color.split(' ')[0]}`} />
                        </div>
                      </div>
                      <div className="ml-4">
                        {/* Click to view role details (read-only) */}
                        <button
                          onClick={() => onEdit?.(role)}
                          className="text-left hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg p-2 -m-2 transition-colors"
                        >
                          <div className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                            {role.role_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            {enhancedRole.is_system_role && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-400">
                                System
                              </span>
                            )}
                            <span className="text-purple-600 dark:text-purple-400">
                              Master Role • Click to view
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* Role Level */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${levelInfo.color}`}>
                      {levelInfo.label}
                    </span>
                  </td>

                  {/* Lead Access */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${scopeInfo.color}`}>
                      {scopeInfo.label}
                    </span>
                  </td>

                  {/* Description */}
                  <td className="px-6 py-4 max-w-xs">
                    <div className="flex items-start space-x-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900 dark:text-white truncate">
                          {role.role_description || 'No description provided'}
                        </p>
                        {role.role_description && role.role_description.length > 50 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Click role name to view full details
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        enhancedRole.is_active !== false 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {enhancedRole.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                      {role.users && role.users.length > 0 && (
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Users className="w-3 h-3 mr-1" />
                          {role.users.length} user{role.users.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {initialData.length === 0 && (
        <div className="text-center py-12">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No roles found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search terms' : 'No master roles available'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.currentPage - 1) * (pagination.itemsPerPage || 20) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * (pagination.itemsPerPage || 20), pagination.totalItems)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{pagination.totalItems}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => onPageChange?.(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNumber = i + 1;
                    const isCurrentPage = pageNumber === pagination.currentPage;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => onPageChange?.(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? 'z-10 bg-brand-50 dark:bg-brand-900/20 border-brand-500 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => onPageChange?.(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Skeleton Loading Component
const RoleTableSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
    {/* Search Skeleton */}
    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
      <div className="flex gap-4">
        <div className="flex-1 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        <div className="w-20 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
      </div>
    </div>
    
    {/* Table Skeleton */}
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            {Array.from({ length: 5 }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: 5 }).map((_, j) => (
                <td key={j} className="px-6 py-4">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default ModernRolesTable;